import { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { storeAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Progress } from '../components/ui/progress';
import { Loader2, Download, CheckCircle2, XCircle, Clock, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function DownloadPage() {
  const { token } = useParams();
  const location = useLocation();
  const allItems = location.state?.allItems;
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const loadInfo = async () => {
      try {
        const res = await storeAPI.downloadInfo(token);
        setInfo(res.data);
      } catch (err) {
        setInfo({ error: 'Invalid or expired download link' });
      } finally {
        setLoading(false);
      }
    };
    loadInfo();
  }, [token]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      window.open(`${backendUrl}/api/download/${token}`, '_blank');
      // Refresh info after download
      setTimeout(async () => {
        try {
          const res = await storeAPI.downloadInfo(token);
          setInfo(res.data);
        } catch (e) { /* Silent refresh - download already initiated */ }
        setDownloading(false);
      }, 2000);
    } catch (err) {
      toast.error('Download failed');
      setDownloading(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-background"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  if (info?.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <XCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Download Unavailable</h2>
            <p className="text-muted-foreground">{info.error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const canDownload = !info.is_expired && !info.is_limit_reached && !info.is_revoked;
  const progressPct = (info.downloads_used / info.max_downloads) * 100;

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-2">
            {canDownload ? (
              <CheckCircle2 className="h-12 w-12 text-emerald-500" />
            ) : (
              <AlertCircle className="h-12 w-12 text-amber-500" />
            )}
          </div>
          <CardTitle className="text-xl">
            {canDownload ? 'Your download is ready!' : 'Download Unavailable'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Product Info */}
          <div className="rounded-lg bg-muted p-4">
            <p className="font-semibold">{info.product_name}</p>
            <Badge variant="secondary" className="mt-1">{info.product_type}</Badge>
          </div>

          {/* Download Status */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Downloads used</span>
              <span className="font-mono">{info.downloads_used} / {info.max_downloads}</span>
            </div>
            <Progress value={progressPct} className="h-2" />
            <p className="text-xs text-muted-foreground">{info.downloads_remaining} downloads remaining</p>
          </div>

          {/* Validity Status */}
          <div className="flex flex-wrap gap-2" data-testid="download-page-validity-badge">
            {info.is_expired && (
              <Badge variant="destructive"><Clock className="h-3 w-3 mr-1" /> Expired</Badge>
            )}
            {info.is_limit_reached && (
              <Badge variant="destructive"><Download className="h-3 w-3 mr-1" /> Limit Reached</Badge>
            )}
            {info.is_revoked && (
              <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" /> Revoked</Badge>
            )}
            {canDownload && (
              <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle2 className="h-3 w-3 mr-1" /> Active</Badge>
            )}
          </div>

          {/* Expiry Info */}
          {info.expiry_at && !info.is_expired && (
            <Alert>
              <Clock className="h-4 w-4" />
              <AlertDescription>
                Link expires on {new Date(info.expiry_at).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </AlertDescription>
            </Alert>
          )}

          {/* Download Button */}
          {canDownload ? (
            <Button className="w-full btn-press" size="lg" onClick={handleDownload} disabled={downloading} data-testid="download-page-download-button">
              {downloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              Download Now
            </Button>
          ) : (
            <div className="text-center text-sm text-muted-foreground">
              <p>This download link is no longer available.</p>
              <p className="mt-1">Please contact the creator for assistance.</p>
            </div>
          )}

          {/* Other items from multi-product checkout */}
          {allItems && allItems.length > 1 && (
            <div className="space-y-2 pt-2">
              <p className="text-sm font-medium text-center">Your other purchases:</p>
              {allItems.filter(item => item.download_token !== token).map(item => (
                <div key={item.download_token} className="flex items-center justify-between p-3 rounded-lg bg-muted">
                  <span className="text-sm">{item.product_name}</span>
                  <Button size="sm" variant="outline" onClick={() => {
                    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
                    window.open(`${backendUrl}/api/download/${item.download_token}`, '_blank');
                  }}>
                    <Download className="h-3.5 w-3.5 mr-1" /> Download
                  </Button>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground pt-2">
            <span>Powered by</span>
            <img src="/logos/funnely-teal.png" alt="Funnely" className="h-4 object-contain" />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
